import React, { useState } from 'react';
import { Affix, Button, Badge, Tooltip } from 'antd';
import { MessageOutlined, CloseOutlined, RobotOutlined } from '@ant-design/icons';

export interface ChatButtonProps {
  onClick: () => void;
  chatOpen?: boolean;
  position?: {
    bottom?: number;
    right?: number;
    left?: number;
    top?: number;
  };
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  badge?: {
    count?: number;
    dot?: boolean;
  };
  tooltipTitle?: string;
  style?: React.CSSProperties;
}

export const ChatButton: React.FC<ChatButtonProps> = ({
  onClick,
  chatOpen = false,
  position = { bottom: 24, right: 24 },
  size = 'large',
  disabled = false,
  badge,
  tooltipTitle,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const buttonSize = size === 'large' ? 56 : size === 'middle' ? 48 : 40;
  const iconSize = size === 'large' ? 24 : size === 'middle' ? 20 : 16;

  const buttonStyle: React.CSSProperties = {
    width: buttonSize,
    height: buttonSize,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: iconSize,
    boxShadow: isHovered 
      ? '0 8px 32px rgba(0, 0, 0, 0.24)' 
      : '0 4px 16px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
    border: 'none',
    background: chatOpen 
      ? 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)'
      : 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
    ...style,
  };

  const affixStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 1000,
    ...position,
  };

  const handleClick = () => {
    if (!disabled) {
      onClick();
    }
  };

  const buttonContent = (
    <Button
      type="primary"
      icon={chatOpen ? <CloseOutlined /> : <MessageOutlined />}
      onClick={handleClick}
      disabled={disabled}
      style={buttonStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    />
  );

  const wrappedButton = badge ? (
    <Badge 
      count={badge.count} 
      dot={badge.dot}
      offset={[-8, 8]}
      style={{
        color: '#fff',
        fontSize: 12,
        height: 20,
        minWidth: 20,
        lineHeight: '20px',
        borderRadius: 10,
      }}
    >
      {buttonContent}
    </Badge>
  ) : (
    buttonContent
  );

  const finalButton = tooltipTitle ? (
    <Tooltip 
      title={tooltipTitle} 
      placement="left"
      mouseEnterDelay={0.5}
    >
      {wrappedButton}
    </Tooltip>
  ) : (
    wrappedButton
  );

  return (
    <Affix style={affixStyle}>
      {finalButton}
      
      {/* Ripple effect animation */}
      {isHovered && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: buttonSize * 1.5,
            height: buttonSize * 1.5,
            marginTop: -(buttonSize * 1.5) / 2,
            marginLeft: -(buttonSize * 1.5) / 2,
            borderRadius: '50%',
            border: '2px solid rgba(24, 144, 255, 0.3)',
            animation: 'ripple 1.5s ease-out infinite',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      )}
      
      {/* CSS Animation */}
      <style>{`
        @keyframes ripple {
          0% {
            transform: scale(0.6);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
    </Affix>
  );
};

export default ChatButton;
